import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
  Grid,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { allIcons } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import { CustomContainer } from "../../../components/Containers";
import NavigationDrawer from "../../../components/NavigationDrawer";
import {
  getCreatorCampaigns,
  participateInCampaign,
} from "../../../services/creator/campaignApiFn";
import { handleOpenSnackBar } from "../../../utils";
import CampaignCard from "./CampaignCard";

const PAGE_LIMIT = 12;

function CreatorCampaigns() {
  const { t } = useTranslation();
  const fetchIdRef = useRef(0);
  const userData = useSelector((state) => state.userData.user);
  const [isLoading, setIsLoading] = useState(false);
  const [requestingId, setRequestingId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 600);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCampaigns = async (pageNum = page) => {
    if (!userData?._id) return;
    const fetchId = ++fetchIdRef.current;
    try {
      setIsLoading(true);
      const response = await getCreatorCampaigns(userData._id, {
        page: pageNum,
        limit: PAGE_LIMIT,
        search: debouncedSearch,
      });
      if (fetchId !== fetchIdRef.current) return;
      if (response?.data?.status === "success") {
        setCampaigns(response.data.data.data || []);
        setTotalPages(response.data.data.pagination?.totalPages || 1);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      handleOpenSnackBar("error", error?.message);
    } finally {
      if (fetchId === fetchIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchCampaigns(1);
  }, [debouncedSearch, userData?._id]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchCampaigns(newPage);
  };

  const handleParticipate = async (campaign) => {
    try {
      setRequestingId(campaign._id);
      const response = await participateInCampaign(userData._id, campaign._id);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        // Sirf isi card ka status update kerte hain, poori list reload nahi
        setCampaigns((prev) =>
          prev.map((item) =>
            item._id === campaign._id
              ? {
                  ...item,
                  participation: {
                    _id: response.data.data?._id,
                    status: "pending",
                    rejectionReason: null,
                  },
                }
              : item,
          ),
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setRequestingId(null);
    }
  };

  const renderSearch = () => (
    <Stack p={2} bgcolor={"primary.light"}>
      <TextField
        size="small"
        sx={{ width: { xs: "100%", md: "35%" }, backgroundColor: "#fff" }}
        placeholder={t("searchByCampaignName")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src={allIcons.searchIcon} width={20} alt="search" />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => setSearch("")}
              />
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <CustomContainer
        primaryHeading={t("availableCampaigns")}
        secondaryHeading={t("availableCampaignsDescription")}
        filterSection={renderSearch()}
      >
        {!isLoading && campaigns.length === 0 ? (
          <Typography textAlign="center" py={5} fontWeight={500}>
            {t("noCampaignsFound")}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} sm={6} lg={4} key={campaign._id}>
                <CampaignCard
                  campaign={campaign}
                  showAction
                  isRequesting={requestingId === campaign._id}
                  onParticipate={handleParticipate}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {totalPages > 1 && (
          <Stack alignItems="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
            />
          </Stack>
        )}
      </CustomContainer>
    </NavigationDrawer>
  );
}

export default CreatorCampaigns;
